import { NavigationGuardNext, RouteLocationNormalized, RouteRecordRaw } from "vue-router"
import DashboardLayout from "@/layouts/dashboard/DashboardLayout.vue"
import GuardianRunner from "@/router/guardian-runner"
import { ensureIsAuthenticated } from "@/router/guardians"
import { useUserStore } from "@/store/user-store"
import { queryClient } from "@/services/query-client"
import { QueryApiCacheKeys } from "@common/config/query-api-cache-keys"
import type { ListHealthWalletAccountsResponse } from "@vitable/asclepius-api-client-ts"

async function redirectToFirstAccount(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  return await GuardianRunner.run(
    to,
    from,
    (result) => {
      if (typeof result === "string" || typeof result === "object") {
        next(result)
        return
      }

      const memberId = useUserStore().memberId

      if (!memberId) {
        next()
        return
      }

      // Get cached data or fetch
      const cachedData = queryClient.getQueryData<ListHealthWalletAccountsResponse>([
        QueryApiCacheKeys.HEALTH_WALLET_ACCOUNTS,
        memberId
      ])

      if (cachedData?.health_wallet_accounts && cachedData.health_wallet_accounts.length > 0) {
        next({
          name: "health-wallet",
          params: { health_wallet_account_id: cachedData.health_wallet_accounts[0].id },
          replace: true
        })
      } else {
        // Let the component handle loading and showing empty state
        next()
      }
    },
    [ensureIsAuthenticated]
  )
}

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    component: DashboardLayout,
    children: [
      {
        path: "health-wallet",
        name: "health-wallet-redirect",
        beforeEnter: redirectToFirstAccount,
        component: () => import("@/modules/health-wallet/pages/health-wallet-page/HealthWalletPage.vue")
      },
      {
        path: "health-wallet/:health_wallet_account_id",
        name: "health-wallet",
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [ensureIsAuthenticated])
        },
        component: () => import("@/modules/health-wallet/pages/health-wallet-page/HealthWalletPage.vue")
      },
      {
        path: "health-wallet/:health_wallet_account_id/reimbursement-requests/:reimbursement_request_id",
        name: "health-wallet-reimbursement-detail",
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [ensureIsAuthenticated])
        },
        component: () => import("@/modules/health-wallet/pages/health-wallet-page/HealthWalletPage.vue")
      }
    ]
  },
  {
    path: "/health-wallet/connect-bank-account",
    name: "connect-bank-account",
    beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
      GuardianRunner.run(to, from, next, [ensureIsAuthenticated])
    },
    component: () => import("@/modules/health-wallet/pages/bank-account-connection-page/BankAccountConnectionPage.vue")
  }
]

export default routes
